/** Firestore access for `skidaCrmActivities` — the per-lead note / event log. */
import {
  addDoc,
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { db } from '@/shared/firebase/client'
import { CRM_COLLECTIONS } from '@/shared/firebase/collections'
import type { Activity } from '@/types/crm'
import { toIsoOrNull } from '@/shared/util/dates'

function mapActivity(snap: QueryDocumentSnapshot<DocumentData>): Activity {
  const d = snap.data()
  return {
    id: snap.id,
    leadId: d.leadId,
    at: toIsoOrNull(d.at) ?? new Date().toISOString(),
    author: d.author ?? 'Unknown',
    text: d.text ?? '',
    source: d.source === 'system' ? 'system' : d.source === 'action' ? 'action' : 'user',
  }
}

/** Live, reverse-chronological activity for one lead. Returns the unsubscribe function. */
export function subscribeToActivity(
  leadId: string,
  onData: (activity: Activity[]) => void,
  onError: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, CRM_COLLECTIONS.activities),
    where('leadId', '==', leadId),
    orderBy('at', 'desc'),
    limit(50),
  )
  return onSnapshot(
    q,
    (snap) => onData(snap.docs.map(mapActivity)),
    (err) => onError(err as Error),
  )
}

/**
 * Append an entry to a lead's log.
 *
 * ## Three sources, because two were not enough — AVV-2026-003
 *
 * `system` is written by Cloud Functions and scripts. The other two both come
 * from a person at this screen and are **not** the same thing:
 *
 *  - `user` — somebody typed it into the note box. This is a note.
 *  - `action` — the log line a *different* control wrote as a side effect:
 *    "Moved to Won", "Owner changed to …", "Lead created", "Qualification
 *    updated to 2/3". Signed by whoever did it, which is why they were all
 *    `user` before, and useful for exactly that reason — but nobody wrote them.
 *
 * They were indistinguishable, and that is half of why sales could not find the
 * notes field: on an active lead a typed note sits among a dozen automatic lines
 * rendered identically. Separating them is what makes "Notes only" mean
 * something.
 *
 * **Entries written before 27 Aug 2026 are all `user`** and cannot be
 * reclassified — the text is the only clue and guessing from it would mislabel
 * somebody's actual note. So the filter is honest going forward and slightly
 * generous about history.
 */
export async function addNote(
  leadId: string,
  author: string,
  text: string,
  source: 'user' | 'action' = 'user',
): Promise<void> {
  await addDoc(collection(db, CRM_COLLECTIONS.activities), {
    leadId,
    author,
    text,
    source,
    at: serverTimestamp(),
  })
}
